import axios from 'axios';
import { isEmpty } from 'lodash';
import * as Sentry from '@sentry/react';
import AuthService from './authService';
import { store } from '../store/store';
import { selectTenantId } from '../pages/configuration/participantSignupSlice';
import { getTenantId } from '../utils/tenantHelpers';
import { convertAllDecimalsToFourPlacesOptimized } from '../utils/global';
import { getDecimalConfig, consumeNextRequestConfig } from '../utils/apiDecimalConfig';

const requestArray: any = [];
const authService = new AuthService();

export const axiosInstance = axios.create({
  url: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '',
});
export const cancelAxios = axios.CancelToken;

let busyApiCall = false;
let isRetrying = false;
let refreshPromise: Promise<string | null> | null = null;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

let sessionKilled = false;

/**
 * Called when a cross-tab logout/new-session event is received.
 * Prevents all future API calls and flushes any queued retries.
 */
export function killSession(): void {
  sessionKilled = true;
  failedQueue.forEach((request) => request.reject(new axios.Cancel('Session ended by another tab')));
  failedQueue = [];
  isRetrying = false;
  refreshPromise = null;
}

export function isSessionKilled(): boolean {
  return sessionKilled;
}

const params = new URLSearchParams(window.location.search);
const type = params.get('type');

const isExternalChatbotApiRequest = (config?: {
  url?: string;
  baseURL?: string;
}): boolean => {
  const requestUrl = config?.url;
  if (typeof requestUrl !== 'string' || requestUrl.length === 0) {
    return false;
  }

  try {
    const parsedUrl = new URL(requestUrl, config?.baseURL || window.location.origin);
    return (
      parsedUrl.hostname.includes('execute-api.') &&
      parsedUrl.hostname.includes('amazonaws.com')
    );
  } catch {
    return false;
  }
};

const isPresignedS3UploadRequest = (config?: {
  method?: string;
  url?: string;
  baseURL?: string;
}): boolean => {
  const method = config?.method?.toLowerCase?.();
  if (method !== 'put') {
    return false;
  }

  const requestUrl = config?.url;
  if (typeof requestUrl !== 'string' || requestUrl.length === 0) {
    return false;
  }

  try {
    const parsedUrl = new URL(requestUrl, config?.baseURL || window.location.origin);
    const isS3Host = parsedUrl.hostname.includes('amazonaws.com');
    const queryParamKeys = Array.from(parsedUrl.searchParams.keys()).map((key) =>
      key.toLowerCase()
    );
    const hasPresignedSignature =
      queryParamKeys.includes('x-amz-signature') ||
      queryParamKeys.includes('x-amz-credential') ||
      queryParamKeys.includes('x-amz-algorithm') ||
      queryParamKeys.includes('awsaccesskeyid') ||
      queryParamKeys.includes('signature');

    return isS3Host && hasPresignedSignature;
  } catch {
    return false;
  }
};

// Process the failed queue with the new token or null if refresh failed
const processQueue = (token: string | null, error: any = null) => {
  console.log(
    `[ProcessQueue] Processing ${failedQueue.length} queued requests: ${token ? 'with new token' : 'failed - no token'
    }`
  );

  failedQueue.forEach((request, index) => {
    if (token) {
      // Retry the request with the new token
      const retryConfig = { ...request.config };

      // Ensure headers exist
      if (!retryConfig.headers) {
        retryConfig.headers = {};
      }

      // Update the Authorization header with the new token
      retryConfig.headers.Authorization = `Bearer ${token}`;

      // Ensure method is preserved (important for POST/PUT requests)
      if (!retryConfig.method && request.config.method) {
        retryConfig.method = request.config.method;
      }

      // Ensure data is preserved for POST/PUT requests
      if (
        (retryConfig.method === 'post' ||
          retryConfig.method === 'put' ||
          retryConfig.method === 'patch') &&
        !retryConfig.data &&
        request.config.data
      ) {
        retryConfig.data = request.config.data;
      }

      // Preserve content-type header for requests with data
      if (retryConfig.data && request.config.headers && request.config.headers['Content-Type']) {
        retryConfig.headers['Content-Type'] = request.config.headers['Content-Type'];
      }

      console.log(
        `[ProcessQueue] Retrying request ${index + 1}/${failedQueue.length} to ${retryConfig.url
        } (${retryConfig.method || 'GET'})`
      );

      axiosInstance(retryConfig)
        .then((response) => {
          console.log(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} succeeded after retry`
          );

          // Convert decimal values in retry response as well
          if (response.data) {
            try {
              // Get config from the original request
              const retryDecimalConfig = getDecimalConfig(retryConfig);
              const retrySkipAll = retryDecimalConfig.skipDecimalConversion;
              const retryExcludeFields = retryDecimalConfig.excludeDecimalFields || [];

              if (!retrySkipAll) {
                response.data = convertAllDecimalsToFourPlacesOptimized(response.data, {
                  maxDepth: 50,
                  inPlace: false,
                  shallow: false,
                  excludeFields: retryExcludeFields,
                });
              }
            } catch (conversionError) {
              console.error('[ProcessQueue] Failed to convert decimal values:', conversionError);
            }
          }

          request.resolve(response);
        })
        .catch((err) => {
          console.error(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} failed after retry:`,
            err.message
          );
          request.reject(err);
        });
    } else {
      // If token refresh failed, reject all requests in queue
      console.log(
        `[ProcessQueue] Rejecting request ${index + 1}/${failedQueue.length
        } due to failed token refresh`
      );
      request.reject(error);
    }
  });

  // Reset the queue and refreshing state
  console.log('[ProcessQueue] Reset queue and state');
  failedQueue = [];
  isRetrying = false;
  refreshPromise = null;
};

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async function (config) {
    if (sessionKilled) {
      return Promise.reject(new axios.Cancel('Session ended by another tab'));
    }

    config.headers = {
      ...config.headers,
    };

    if (isPresignedS3UploadRequest(config)) {
      return config;
    }

    const isChatbotApi = isExternalChatbotApiRequest(config);
    const tenantId = getTenantId();

    if (!isChatbotApi) {
      if (!config.headers['X-Tenant-Id'] && tenantId) {
        config.headers['X-Tenant-Id'] = tenantId;
      }
    } else {
      delete config.headers['X-Switch-Source-Role-Id'];
      delete config.headers['x-switch-source-role-id'];
      delete config.headers.bearerToken;

      if (tenantId) {
        config.headers['X-Tenant-Id'] = tenantId;
      } else {
        delete config.headers['X-Tenant-Id'];
        delete config.headers['x-tenant-id'];
      }
    }

    // Attach decimal conversion config to axios config
    const decimalConfig = consumeNextRequestConfig();
    if (decimalConfig) {
      (config as any).__decimalConfig = decimalConfig;
    }

    // Check if token is expired and refresh it before making the request
    if (authService.isTokenExpired() && !type) {
      console.log('[Request Interceptor] Token is expired, attempting to refresh');
      try {
        const newToken = await authService.signInSilent();
        if (newToken) {
          console.log('[Request Interceptor] Token refreshed successfully');
          // Add the new token to the request
          config.headers.Authorization = `Bearer ${newToken}`;
        } else {
          console.log('[Request Interceptor] Token refresh returned null');
        }
      } catch (error) {
        console.error('[Request Interceptor] Failed to refresh token:', error);
        // Continue with the request - the response interceptor will handle 401 errors
      }
    } else {
      // If token is valid, add it to the request
      const token = authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  async function (error: any) {
    return await Promise.reject(error);
  }
);

// Helper to detect network/CORS errors (API down scenarios)
const isNetworkError = (error: any): boolean => {
  // Network errors occur when:
  // 1. No response is received (API down, CORS blocked, network failure)
  // 2. Error code indicates network issue
  const networkErrorCodes = [
    'ERR_NETWORK',
    'ECONNABORTED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'ECONNREFUSED',
  ];
  return (
    !error.response &&
    error.request &&
    (error.message === 'Network Error' ||
      error.code === 'ERR_NETWORK' ||
      networkErrorCodes.includes(error.code))
  );
};

// Helper to get error type for categorization
const getErrorType = (error: any): string => {
  if (axios.isCancel(error)) return 'CANCELLED';
  if (isNetworkError(error)) return 'NETWORK_ERROR';
  if (error.code === 'ECONNABORTED') return 'TIMEOUT';
  if (error.response) return `HTTP_${error.response.status}`;
  return 'UNKNOWN';
};

const isMaintenanceFallbackError = (error: any): boolean => {
  if (axios.isCancel(error)) return false;
  if (isNetworkError(error)) return true;
  const status = error.response?.status;
  if (!status) return true;
  if (status === 401 || status === 403) return false;
  if (status >= 500) return true;
  const body = error.response?.data;
  if (!body?.error?.message && !body?.error?.validationErrors) return true;
  return false;
};

const scrubHeaders = (headers: any): Record<string, any> | undefined => {
  if (!headers) return undefined;
  const scrubbed = { ...headers };
  const sensitiveKeys = ['authorization', 'bearertoken', 'cookie', 'set-cookie'];
  for (const key of Object.keys(scrubbed)) {
    if (sensitiveKeys.includes(key.toLowerCase())) {
      scrubbed[key] = '[REDACTED]';
    }
  }
  return scrubbed;
};

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    if (requestArray.length !== 0) {
      requestArray.forEach(function (x: any, i: any) {
        if (response.config.url === x.url) {
          requestArray.splice(i, 1);
        }
      });
    }
    // Get decimal conversion config from the request
    const decimalConfig = getDecimalConfig(response.config);
    const shouldSkipAll = decimalConfig.skipDecimalConversion;
    const excludeFields = decimalConfig.excludeDecimalFields || [];

    // Skip decimal conversion for binary responses (e.g. PDF downloads)
    const isBlobResponse =
      response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer';
    const isChatbotApiResponse = isExternalChatbotApiRequest(response.config);

    if (response.data && !shouldSkipAll && !isBlobResponse && !isChatbotApiResponse) {
      try {
        response.data = convertAllDecimalsToFourPlacesOptimized(response.data, {
          maxDepth: 50, // Limit depth to prevent stack overflow
          inPlace: false, // Create new object to avoid mutation
          shallow: false, // Deep conversion for nested structures
          excludeFields: excludeFields, // Fields to skip conversion (keep raw values)
        });
      } catch (error) {
        // If conversion fails, log error but return original response
        console.error('[API Interceptor] Failed to convert decimal values:', error);
      }
    }

    return response;
  },
  async function (error: any) {
    if (sessionKilled) {
      return Promise.reject(new axios.Cancel('Session ended by another tab'));
    }

    const originalRequest = error.config;
    if (isPresignedS3UploadRequest(originalRequest)) {
      return await Promise.reject(error);
    }

    const correlationId =
      error.response?.headers?.['x-error-correlation-id'] ||
      error.response?.data?.correlationId;
    const errorType = getErrorType(error);
    const isNetworkOrCorsError = isNetworkError(error);
    const isFallback = isMaintenanceFallbackError(error);

    if (isFallback) {
      const tenantId = selectTenantId(store.getState());

      Sentry.captureException(error, {
        level: 'error',
        tags: {
          error_type: 'wl_maintenance_fallback',
          correlation_id: correlationId || 'N/A',
          httpStatus: error.response?.status?.toString() || 'NO_RESPONSE',
          endpoint: error.config?.url || 'unknown',
          requestMethod: error.config?.method?.toUpperCase() || 'UNKNOWN',
          isNetworkError: String(isNetworkOrCorsError),
          tenantId: tenantId || 'N/A',
        },
        extra: {
          errorType,
          errorMessage: error.message,
          errorCode: error.code,
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
          requestBaseURL: error.config?.baseURL,
          requestHeaders: scrubHeaders(error.config?.headers),
          responseStatus: error.response?.status,
          responseData: error.response?.data
            ? JSON.stringify(error.response.data, null, 2)
            : undefined,
          responseHeaders: scrubHeaders(error.response?.headers),
          currentPath: window.location.pathname,
          timestamp: new Date().toISOString(),
          ...(isNetworkOrCorsError && {
            networkErrorDetails: {
              message: error.message,
              code: error.code,
              possibleCause:
                'API may be down, network failure, or CORS blocked due to no response headers',
              hasRequest: !!error.request,
              hasResponse: !!error.response,
            },
          }),
        },
        fingerprint: isNetworkOrCorsError
          ? ['wl-maintenance-fallback', 'network-error', error.config?.url || 'unknown-url']
          : ['wl-maintenance-fallback', error.config?.url || 'unknown-url'],
      });
    }

    if (error.response?.status === 400 || error.response?.status === 401) {
      const tenantId = selectTenantId(store.getState());
      const status = error.response.status;
      const errorTag = status === 400 ? 'wl_bad_request' : 'wl_unauthorized';

      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          error_type: errorTag,
          correlation_id: correlationId || 'N/A',
          httpStatus: String(status),
          endpoint: error.config?.url || 'unknown',
          requestMethod: error.config?.method?.toUpperCase() || 'UNKNOWN',
          tenantId: tenantId || 'N/A',
        },
        extra: {
          errorType,
          errorMessage: error.message,
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
          requestData: (() => {
            try {
              return error.config?.data
                ? JSON.stringify(JSON.parse(error.config.data), null, 2)
                : undefined;
            } catch {
              return error.config?.data;
            }
          })(),
          requestHeaders: scrubHeaders(error.config?.headers),
          responseStatus: status,
          responseData: error.response?.data
            ? JSON.stringify(error.response.data, null, 2)
            : undefined,
          validationErrors: error.response?.data?.error?.validationErrors || null,
          errorDetail: error.response?.data?.error?.message || null,
          currentPath: window.location.pathname,
          timestamp: new Date().toISOString(),
        },
        fingerprint: [errorTag, error.config?.url || 'unknown-url'],
      });
    }

    // Store the original request
    requestArray.push(originalRequest);

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log('[Response Interceptor] 401 Unauthorized error, queueing request for retry');
      const retryRequest = new Promise((resolve, reject) => {
        // Add to queue to retry after token refresh
        failedQueue.push({
          resolve,
          reject,
          config: originalRequest,
        });
      });

      // If we're not already refreshing the token, initiate refresh
      if (!isRetrying) {
        console.log('[Response Interceptor] Starting token refresh');
        isRetrying = true;

        // Create a promise for the token refresh that can be reused
        refreshPromise = new Promise<string | null>((resolve) => {
          authService
            .signInSilent()
            .then((token) => {
              // We got a new token, resolve with it
              console.log('[Response Interceptor] Token refresh successful');
              resolve(token as string);
            })
            .catch((refreshError) => {
              // Failed to get token
              console.error('[Response Interceptor] Failed to refresh token:', refreshError);
              resolve(null);
            });
        });

        // Process the token result
        refreshPromise.then((accessToken) => {
          if (accessToken) {
            console.log('[Response Interceptor] Processing retry queue with new token');
            // Process the queue with the new token
            processQueue(accessToken);
          } else {
            console.log('[Response Interceptor] No token obtained, rejecting all queued requests');
            // If we couldn't get a new token, reject all requests
            processQueue(null, error);

            // If no active API call is in progress, dispatch SIGN_IN event
            if (!busyApiCall) {
              busyApiCall = true;
              console.log('[Response Interceptor] Dispatching SIGN_IN event');
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'SIGN_IN' }));
              }, 1000);
            }
          }
        });
      } else {
        console.log('[Response Interceptor] Token refresh already in progress, request queued');
      }

      // Return the promise that will resolve/reject based on the token refresh
      return retryRequest;
    }

    if (error.response?.status === 403 && isEmpty(error?.response?.data?.error)) {
      if (!busyApiCall) {
        busyApiCall = true;
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'REFRESH' }));
        }, 1000);
      }
    }

    return await Promise.reject(error);
  }
);
